import { ArrowRight } from "lucide-react";
import NotificationHeaderBadge from "@/pages/notifications/components/NotificationHeaderBadge";
import MarkAllReadButton from "@/pages/notifications/components/MarkAllReadButton";
import GroupedNotificationList from "@/pages/notifications/components/GroupedNotificationList";
import EmptyState from "@/pages/notifications/components/EmptyState";
import { Spinner } from "@/components/ui/spinner";
import { groupByRecency } from "@/pages/notifications/utils";
import type { NotificationStatus } from "@/contexts/NotificationsProvider";
import type { Notification } from "@/pages/notifications/types";

const RECENT_LIMIT = 8;

export interface NotificationDropdownProps {
  notifications: Notification[];
  initialLoading: boolean;
  getStatus: (n: Notification) => NotificationStatus;
  onMarkRead: (n_id: string) => void;
  onMarkAllRead: () => void;
  onViewAll: () => void;
}

function NotificationDropdown({
  notifications,
  initialLoading,
  getStatus,
  onMarkRead,
  onMarkAllRead,
  onViewAll,
}: NotificationDropdownProps) {
  // Unread items sort first so they aren't pushed out of the RECENT_LIMIT slice.
  const unread = notifications.filter((n) => !n.seen_on);
  const read = notifications.filter((n) => n.seen_on);
  const visible = [...unread, ...read].slice(0, RECENT_LIMIT);
  const visibleUnreadCount = visible.filter((n) => !n.seen_on).length;
  const groups = groupByRecency(visible);

  return (
    <div className="flex max-h-[480px] flex-col">
      <div className="flex items-center justify-between border-b border-border-light px-4 py-3">
        <NotificationHeaderBadge
          unreadCount={visibleUnreadCount}
          unreadSuffix={unread.length > visibleUnreadCount ? "+" : ""}
          unreadLabel="new"
          size="sm"
        />
        {notifications.length > 0 && (
          <MarkAllReadButton onClick={onMarkAllRead} />
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {initialLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner size={22} />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState size="sm" />
        ) : (
          <GroupedNotificationList
            groups={groups}
            getStatus={getStatus}
            onMarkRead={onMarkRead}
            size="sm"
          />
        )}
      </div>

      <div className="border-t border-border-light px-3 py-2.5">
        <button
          type="button"
          onClick={onViewAll}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-light py-2 text-center text-xs font-bold text-primary hover:bg-primary-medium"
        >
          View all notifications
          <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}

export default NotificationDropdown;
